import React, { memo } from 'react'
import { Box, Checkbox, TableCell, TableRow } from '@mui/material'
import { AGE, C, MONO, tnum } from './tokens'
import { ageLevel } from './model'
import { AgeBadge, RestaurantLogo, StatusBadge } from './Badges'
import { AmountCell, CustomerCell, DeliveryCell, RowActions } from './cells'
import EtaTimeline from './EtaTimeline'
import { useDispatchUi } from './context'

/** Hover-only controls (row menu, address copy/map) stay visible on touch screens and while focused. */
export const revealSx = {
  '& .dispatch-reveal': { opacity: 0, transition: 'opacity 120ms' },
  '&:hover .dispatch-reveal, &:focus-within .dispatch-reveal': { opacity: 1 },
  '@media (hover: none)': { '& .dispatch-reveal': { opacity: 1 } },
}

const cellSx = { px: 1.25, py: 0, borderBottom: `1px solid ${C.borderSoft}` }
const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }

function OrderRow({ view, columns, density, selected, highlighted, onToggle, onHover }) {
  const ui = useDispatchUi()
  const level = ageLevel(view, ui.now)
  const compact = density === 'compact'
  const open = ui.openId === view.id
  const show = c => columns.has(c)

  return (
    <TableRow
      tabIndex={0}
      aria-selected={selected}
      onClick={() => ui.openOrder(view.id)}
      onKeyDown={e => {
        if (e.key === 'Enter' && e.target === e.currentTarget) ui.openOrder(view.id)
      }}
      onMouseEnter={() => onHover(view.id)}
      onMouseLeave={() => onHover(null)}
      sx={{
        height: compact ? 40 : 56,
        cursor: 'pointer',
        outline: 'none',
        transition: 'background-color 120ms',
        bgcolor: selected ? 'rgba(238,244,255,0.7)' : open || highlighted ? 'rgba(242,244,247,0.9)' : 'transparent',
        '&:hover': { bgcolor: selected ? 'rgba(238,244,255,1)' : 'rgba(242,244,247,0.6)' },
        '&:focus-visible': { boxShadow: 'inset 0 0 0 2px rgba(141,151,168,0.5)' },
        ...revealSx,
      }}
    >
      <TableCell padding="none" sx={{ ...cellSx, position: 'relative', pl: 1.5, pr: 0 }} onClick={e => e.stopPropagation()}>
        <Box component="span" aria-hidden sx={{ position: 'absolute', left: 0, top: 6, bottom: 6, width: 3, borderRadius: '0 3px 3px 0', bgcolor: AGE[level].bar }} />
        <Checkbox
          size="small"
          checked={selected}
          onClick={e => onToggle(view.id, e.shiftKey)}
          inputProps={{ 'aria-label': `Select order #${view.orderNo}` }}
          sx={{ p: 0.5, color: C.input, '&.Mui-checked': { color: C.ink } }}
        />
      </TableCell>

      {show('age') && (
        <TableCell sx={cellSx}>
          <AgeBadge view={view} level={level} now={ui.now} />
        </TableCell>
      )}

      {show('order') && (
        <TableCell sx={cellSx}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, whiteSpace: 'nowrap' }}>
            <Box component="span" sx={{ fontFamily: MONO, fontSize: 13, fontWeight: 500, letterSpacing: '-0.01em', color: C.text, ...tnum }}>
              #{view.orderNo}
            </Box>
            {!compact && (
              <Box component="span" sx={{ fontSize: 12, color: C.muted }}>
                {view.typeLabel}
                {view.sourceLabel
                  ? ` · ${view.sourceLabel}`
                  : view.raw.paymentMethod ? ` · ${String(view.raw.paymentMethod).toUpperCase() === 'COD' ? 'Cash' : 'Card'}` : ''}
              </Box>
            )}
          </Box>
        </TableCell>
      )}

      {show('restaurant') && (
        <TableCell sx={cellSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <RestaurantLogo restaurant={view.restaurant} />
            <Box component="span" sx={{ fontSize: 13, fontWeight: 500, color: C.text, ...ellipsis }}>{view.restaurant.name}</Box>
          </Box>
        </TableCell>
      )}

      {show('status') && (
        <TableCell sx={{ ...cellSx, overflow: 'hidden' }}>
          <StatusBadge status={view.status} />
        </TableCell>
      )}

      {show('eta') && (
        <TableCell sx={cellSx}>
          <EtaTimeline view={view} now={ui.now} compact={compact} />
        </TableCell>
      )}

      {show('delivery') && (
        <TableCell sx={{ ...cellSx, overflow: 'hidden' }}>
          <DeliveryCell view={view} compact={compact} />
        </TableCell>
      )}

      {show('customer') && (
        <TableCell sx={{ ...cellSx, overflow: 'hidden' }}>
          <CustomerCell view={view} compact={compact} />
        </TableCell>
      )}

      {show('amount') && (
        <TableCell sx={{ ...cellSx, textAlign: 'right' }}>
          <AmountCell view={view} compact={compact} />
        </TableCell>
      )}

      <TableCell sx={{ ...cellSx, pl: 0, pr: 1, textAlign: 'right' }}>
        <RowActions view={view} />
      </TableCell>
    </TableRow>
  )
}

export default memo(OrderRow)
